/**
 * Eleven Labs Text-to-Speech Service
 * 
 * This service plays pre-generated tooltip audio files from static assets.
 * Audio files are generated once using the generateTooltipAudio.js script
 * and served to all users, ensuring consistent voice quality and zero API calls.
 * 
 * For dynamic text-to-speech (fallback), it can use the Eleven Labs API with caching.
 */

const ELEVEN_LABS_API_KEY = process.env.REACT_APP_ELEVEN_LABS_API_KEY;
const ELEVEN_LABS_VOICE_ID = process.env.REACT_APP_ELEVEN_LABS_VOICE_ID || '21m00Tcm4TlvDq8ikWAM';
const ELEVEN_LABS_API_URL = 'https://api.elevenlabs.io/v1/text-to-speech';

// Audio files configuration
const STATIC_AUDIO_BASE_PATH = '/audio/tooltips'; // Local fallback
const AZURE_STORAGE_ACCOUNT_NAME = process.env.REACT_APP_AZURE_STORAGE_ACCOUNT_NAME;
const AZURE_STORAGE_CONTAINER_NAME = process.env.REACT_APP_AZURE_STORAGE_CONTAINER_NAME || 'audio';
const USE_AZURE_STORAGE = !!AZURE_STORAGE_ACCOUNT_NAME;

// IndexedDB database name and version (for dynamic TTS fallback)
const DB_NAME = 'TooltipAudioCache';
const DB_VERSION = 1;
const STORE_NAME = 'audioCache';

/**
 * Initialize IndexedDB database for audio caching
 */
const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'textHash' });
        objectStore.createIndex('textHash', 'textHash', { unique: true });
      }
    };
  });
};

/**
 * Generate a hash from text for cache key
 */
const hashText = (text) => {
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    const char = text.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString();
};

/**
 * Get cached audio from IndexedDB
 */
const getCachedAudio = async (textHash) => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(textHash);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const result = request.result;
        if (result && result.audioBlob) {
          // Convert base64 back to blob
          const byteCharacters = atob(result.audioBlob);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'audio/mpeg' });
          resolve(blob);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting cached audio:', error);
    return null;
  }
};

/**
 * Cache audio in IndexedDB
 */
const cacheAudio = async (textHash, audioBlob) => {
  try {
    // Convert blob to base64 FIRST, before starting transaction
    const base64data = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:audio/mpeg;base64, prefix
        resolve(base64);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(audioBlob);
    });

    // NOW start the transaction with the base64 data ready
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const data = {
      textHash,
      audioBlob: base64data,
      timestamp: Date.now(),
    };

    return new Promise((resolve, reject) => {
      const request = store.put(data);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error caching audio:', error);
  }
};

/**
 * Generate speech audio using Eleven Labs TTS API
 */
const generateSpeech = async (text, voiceId = ELEVEN_LABS_VOICE_ID) => {
  if (!ELEVEN_LABS_API_KEY) {
    throw new Error('Eleven Labs API key is not configured. Please set REACT_APP_ELEVEN_LABS_API_KEY in your environment variables.');
  }

  try {
    const response = await fetch(`${ELEVEN_LABS_API_URL}/${voiceId}`, {
      method: 'POST',
      headers: {
        'Accept': 'audio/mpeg',
        'Content-Type': 'application/json',
        'xi-api-key': ELEVEN_LABS_API_KEY,
      },
      body: JSON.stringify({
        text,
        model_id: 'eleven_monolingual_v1',
        voice_settings: {
          stability: 0.5,
          similarity_boost: 0.75,
        },
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Eleven Labs API error: ${response.status} - ${errorData.detail?.message || response.statusText}`);
    }

    const audioBlob = await response.blob();
    return audioBlob;
  } catch (error) {
    console.error('Error generating speech:', error);
    throw error;
  }
};

/**
 * Get speech audio for a tooltip by ID (from Azure Storage or static files)
 * 
 * @param {string} tooltipId - The tooltip ID (e.g., 'bio-tooltip')
 * @returns {Promise<Blob>} - Audio blob ready to play
 */
export const getTooltipAudio = async (tooltipId) => {
  if (!tooltipId) {
    throw new Error('Tooltip ID cannot be empty');
  }

  // Try Azure Storage first (if configured)
  if (USE_AZURE_STORAGE) {
    // Load version from version.json to bust cache when files are regenerated
    let version = '1';
    try {
      const versionResponse = await fetch('/audio/tooltips/version.json', { cache: 'no-cache' });
      if (versionResponse.ok) {
        const versionData = await versionResponse.json();
        version = versionData.version || '1';
      }
    } catch (error) {
      // If version file doesn't exist, use default version
      console.warn('Version file not found, using default cache version');
    }
    
    // Add version parameter that only changes when files are regenerated
    const azureUrl = `https://${AZURE_STORAGE_ACCOUNT_NAME}.blob.core.windows.net/${AZURE_STORAGE_CONTAINER_NAME}/tooltips/${tooltipId}.mp3?v=${version}`;
    
    try {
      console.log(`Fetching audio for ${tooltipId} from: ${azureUrl}`);
      const response = await fetch(azureUrl, {
        cache: 'default' // Allow normal browser caching
      });
      if (response.ok) {
        const audioBlob = await response.blob();
        console.log(`✅ Using Azure Storage audio for tooltip: ${tooltipId} (version: ${version})`);
        return audioBlob;
      } else {
        console.warn(`❌ Azure Storage fetch failed for ${tooltipId}: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.warn(`Azure Storage audio not found for ${tooltipId}, trying local fallback`);
    }
  }

  // Fallback to local static files
  const audioPath = `${STATIC_AUDIO_BASE_PATH}/${tooltipId}.mp3`;
  
  try {
    const response = await fetch(audioPath);
    if (response.ok) {
      const audioBlob = await response.blob();
      console.log(`Using local static audio for tooltip: ${tooltipId}`);
      return audioBlob;
    }
  } catch (error) {
    console.warn(`Local static audio not found for ${tooltipId}`);
  }

  // Final fallback: If files don't exist, throw error
  throw new Error(`Audio file not found for tooltip: ${tooltipId}. Please run 'npm run generate-tooltip-audio' to generate audio files.`);
};

/**
 * Get speech audio for text (with caching) - for dynamic content
 * 
 * @param {string} text - The text to convert to speech
 * @param {string} voiceId - Optional voice ID (defaults to configured voice)
 * @returns {Promise<Blob>} - Audio blob ready to play
 */
export const getSpeechAudio = async (text, voiceId = ELEVEN_LABS_VOICE_ID) => {
  if (!text || text.trim().length === 0) {
    throw new Error('Text cannot be empty');
  }

  // Generate hash for cache key
  const textHash = hashText(text);

  // Try to get from cache first
  const cachedAudio = await getCachedAudio(textHash);
  if (cachedAudio) {
    console.log('Using cached audio for dynamic text');
    return cachedAudio;
  }

  // Generate new audio if not cached
  console.log('Generating new audio for dynamic text');
  const audioBlob = await generateSpeech(text, voiceId);

  // Cache the audio for future use
  await cacheAudio(textHash, audioBlob);

  return audioBlob;
};

/**
 * Play audio blob
 * 
 * @param {Blob} audioBlob - Audio blob to play
 * @returns {Promise<HTMLAudioElement>} - Audio element that can be controlled
 */
export const playAudio = (audioBlob) => {
  return new Promise((resolve, reject) => {
    const audioUrl = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioUrl);

    // Normalize volume and playback rate for consistency across all tooltips
    audio.volume = 1.0; // Set to maximum (1.0) for consistent volume
    audio.playbackRate = 1.0; // Ensure consistent playback speed

    // Resolve immediately with audio element
    resolve(audio);

    audio.onerror = (error) => {
      URL.revokeObjectURL(audioUrl);
      reject(error);
    };

    audio.onended = () => {
      // Clean up the object URL after playback
      setTimeout(() => URL.revokeObjectURL(audioUrl), 100);
    };

    // Start playing
    audio.play().catch((error) => {
      URL.revokeObjectURL(audioUrl);
      reject(error);
    });
  });
};

/**
 * Check if tooltip audio files are available (static files)
 * This doesn't require API key since we use pre-generated files
 */
export const isConfigured = () => {
  // Static files don't require API key - they're pre-generated
  // We'll check if files exist when trying to play them
  return true; // Always return true, will fallback to Web Speech if files don't exist
};

